/**
 * Tela /catalog/queue (TH-53359 / M5.E5.S2.1).
 *
 * Fila do operador — exibe 1 produto pendente por vez, com upload
 * drag&drop + preview lado a lado (original vs rembg).
 *
 * Layout: card grande centralizado, focado em produtividade. Atalhos
 * de teclado (TH-53360) cobrem aprovar/rejeitar/marcar/próximo. Painel
 * `?` mostra atalhos on-screen.
 *
 * Endpoints usados:
 * - GET /api/catalog/queue       — lista paginada de pendentes
 * - GET /api/catalog/:id/preview — original + processado lado a lado
 * - POST /api/catalog/:id/image  — upload (TH-53349) — backend enfileira
 *                                  worker rembg + atualiza master_image_url
 * - POST /api/catalog/:id/approve {decision} — aprovar/rejeitar
 * - POST /api/catalog/:id/mark-not-available — fallback sólido
 *
 * Meta: ~50 produtos/dia. Contador no header mostra "Processados hoje".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

interface CatalogQueueItem {
  id: string;
  ean: string | null;
  name: string;
  brand: string | null;
  category_slug: string | null;
  unit: string | null;
  master_image_url: string | null;
  master_image_quality: string;
  ai_hint: string | null;
  source: string | null;
  waiting_tenants: number;
}

interface QueueResponse {
  data: CatalogQueueItem[];
  meta: { page: number; per_page: number; total: number };
}

interface PreviewResponse {
  current: {
    master_image_url: string | null;
    master_image_quality: string;
  };
  proposed: {
    master_image_url: string | null;
    change_type: string;
  } | null;
}

const PER_PAGE = 25;
const PROCESSED_KEY = "catalog-queue-processed-today";

async function fetchQueue(page: number): Promise<QueueResponse> {
  const res = await fetch(
    `/api/catalog/queue?page=${page}&per_page=${PER_PAGE}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as QueueResponse;
}

async function fetchPreview(id: string): Promise<PreviewResponse> {
  const res = await fetch(`/api/catalog/${id}/preview`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PreviewResponse;
}

async function uploadImage(id: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/catalog/${id}/image`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`HTTP ${res.status}: ${detail}`);
  }
}

async function approve(id: string, decision: "approve" | "reject", reason?: string) {
  const res = await fetch(`/api/catalog/${id}/approve`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      decision,
      ...(decision === "reject" ? { rejection_reason: reason ?? "rejeitado pelo operador" } : {}),
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

async function markNotAvailable(id: string) {
  const res = await fetch(`/api/catalog/${id}/mark-not-available`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/** Counter persistido em localStorage, dia local. Reseta à meia-noite. */
function useProcessedToday(): readonly [number, () => void] {
  const today = useMemo(() => new Date().toLocaleDateString("pt-BR"), []);
  const [count, setCount] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = window.localStorage.getItem(PROCESSED_KEY);
      if (!raw) return 0;
      const parsed = JSON.parse(raw) as { day: string; n: number };
      return parsed.day === today ? parsed.n : 0;
    } catch {
      return 0;
    }
  });

  const inc = useCallback(() => {
    setCount((prev) => {
      const next = prev + 1;
      try {
        window.localStorage.setItem(
          PROCESSED_KEY,
          JSON.stringify({ day: today, n: next }),
        );
      } catch {
        /* localStorage indisponível — silencia */
      }
      return next;
    });
  }, [today]);

  return [count, inc];
}

export function CatalogQueuePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canRead =
    user?.role === "super_admin" || user?.role === "catalog_operator";

  const [page] = useState(1);
  const [showHelp, setShowHelp] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [processedToday, incrementProcessed] = useProcessedToday();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const queueQuery = useQuery({
    queryKey: ["catalog-queue", page],
    queryFn: () => fetchQueue(page),
    enabled: canRead,
  });

  const current = queueQuery.data?.data[0];
  const remaining = queueQuery.data?.meta.total ?? 0;

  const previewQuery = useQuery({
    queryKey: ["catalog-preview", current?.id],
    queryFn: () => fetchPreview(current!.id),
    enabled: !!current?.id,
  });

  const next = useCallback(() => {
    // Move pro próximo item recarregando a queue. O backend re-prioriza
    // por waiting_tenants — operador pode acabar voltando ao mesmo item
    // se ainda for top priority, mas isso é OK (provavelmente operador
    // saiu do meio e voltou).
    queryClient.invalidateQueries({ queryKey: ["catalog-queue"] });
  }, [queryClient]);

  const finishAction = useCallback(() => {
    incrementProcessed();
    next();
  }, [incrementProcessed, next]);

  const approveMutation = useMutation({
    mutationFn: () => approve(current!.id, "approve"),
    onSuccess: finishAction,
    onError: (err: Error) => setActionError(err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: () => approve(current!.id, "reject", "refazer no operador"),
    onSuccess: finishAction,
    onError: (err: Error) => setActionError(err.message),
  });

  const markNAMutation = useMutation({
    mutationFn: () => markNotAvailable(current!.id),
    onSuccess: finishAction,
    onError: (err: Error) => setActionError(err.message),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadImage(current!.id, file),
    onSuccess: () => {
      // Recarrega preview pra mostrar processed.png assim que o worker
      // terminar (operador deve dar refresh manual ou esperar 1-2s; a
      // queue não tira esse item até `approve`).
      queryClient.invalidateQueries({ queryKey: ["catalog-preview", current?.id] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // Atalhos de teclado (TH-53360)
  useKeyboardShortcuts(
    {
      a: () => current && !approveMutation.isPending && approveMutation.mutate(),
      r: () => current && !rejectMutation.isPending && rejectMutation.mutate(),
      x: () => current && !markNAMutation.isPending && markNAMutation.mutate(),
      ArrowRight: () => next(),
      "?": () => setShowHelp((v) => !v),
      Escape: () => setShowHelp(false),
    },
    { enabled: canRead && !!current },
  );

  // Limpa erro quando o usuário avança.
  useEffect(() => {
    setActionError(null);
  }, [current?.id]);

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CATÁLOGO / FILA" title="Acesso negado">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Esta tela é exclusiva de super_admin e catalog_operator.
          </p>
        </AppLayout>
      </RequireAuth>
    );
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!current) return;
    const file = e.dataTransfer.files[0];
    if (file) uploadMutation.mutate(file);
  };

  const onFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && current) uploadMutation.mutate(file);
    e.target.value = ""; // permite re-upload do mesmo arquivo
  };

  const headerActions = (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-[var(--color-muted-foreground)]">
        Processados hoje: <strong>{processedToday}</strong> / meta ~50
      </span>
      <Button size="sm" variant="ghost" onClick={() => setShowHelp((v) => !v)}>
        ? Atalhos
      </Button>
    </div>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="CATÁLOGO / FILA"
        title={`Fila do operador (${remaining} pendentes)`}
        actions={headerActions}
      >
        <div className="mx-auto max-w-4xl">
          {showHelp && (
            <Card className="mb-4 p-4">
              <h3 className="mb-2 text-sm font-semibold">Atalhos</h3>
              <ul className="grid grid-cols-2 gap-2 text-sm text-[var(--color-muted-foreground)]">
                <li>
                  <kbd>A</kbd> — Aprovar
                </li>
                <li>
                  <kbd>R</kbd> — Refazer (rejeitar)
                </li>
                <li>
                  <kbd>X</kbd> — Marcar sem imagem
                </li>
                <li>
                  <kbd>→</kbd> — Próximo
                </li>
                <li>
                  <kbd>?</kbd> — Mostrar/ocultar ajuda
                </li>
                <li>
                  <kbd>Esc</kbd> — Fechar ajuda
                </li>
              </ul>
            </Card>
          )}

          {queueQuery.isLoading && <Skeleton className="h-96" />}

          {!queueQuery.isLoading && !current && (
            <Card className="p-8 text-center">
              <p className="text-lg font-medium">Fila vazia.</p>
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                Nenhum produto pendente de revisão.
              </p>
            </Card>
          )}

          {current && (
            <Card className="p-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-semibold">{current.name}</h2>
                  <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    EAN <span className="font-mono">{current.ean ?? "—"}</span> ·{" "}
                    {current.brand ?? "Sem marca"} · {current.category_slug ?? "Sem categoria"} ·{" "}
                    {current.waiting_tenants} tenant
                    {current.waiting_tenants === 1 ? "" : "s"} esperando
                  </div>
                </div>
                {current.source && (
                  <span className="text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                    {current.source}
                  </span>
                )}
              </div>

              {actionError && (
                <div role="alert" className="mb-4 text-sm text-[var(--color-destructive)]">
                  {actionError}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                    Original (Cosmos)
                  </div>
                  {previewQuery.data?.current.master_image_url ? (
                    <img
                      src={previewQuery.data.current.master_image_url}
                      alt="Imagem original"
                      className="h-64 w-full rounded border border-[var(--color-border)] object-contain"
                    />
                  ) : (
                    <div className="flex h-64 items-center justify-center rounded border border-dashed border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)]">
                      Sem imagem ainda
                    </div>
                  )}
                </div>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-64 cursor-pointer flex-col items-center justify-center rounded border-2 border-dashed border-[var(--color-border)] text-center text-sm text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  {uploadMutation.isPending ? (
                    <p>Enviando…</p>
                  ) : previewQuery.data?.proposed?.master_image_url ? (
                    <img
                      src={previewQuery.data.proposed.master_image_url}
                      alt="Proposto (rembg)"
                      className="h-full w-full rounded object-contain"
                    />
                  ) : (
                    <div className="space-y-1 p-4">
                      <p>Arraste uma imagem aqui</p>
                      <p className="text-xs">ou clique para escolher (jpg/png, ≤5 MB)</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={onFilePick}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Button
                  size="lg"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isPending}
                  title="Aprovar (A)"
                >
                  Aprovar
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => rejectMutation.mutate()}
                  disabled={rejectMutation.isPending}
                  title="Refazer (R)"
                >
                  Refazer
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => markNAMutation.mutate()}
                  disabled={markNAMutation.isPending}
                  title="Marcar sem imagem (X)"
                >
                  Sem imagem
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => next()}
                  title="Próximo (→)"
                >
                  Próximo
                </Button>
              </div>
            </Card>
          )}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
